// packages/core/src/learning/core-modules/safety-before-speed.ts
// ─────────────────────────────────────────────────────────────────────────
// NISK CORE PATH — Module 05: "Safety Before Speed" (tier: stability)
// 5 lessons + checkpoint quiz + final test. Assembled to the real
// Lesson / ModuleAssessment / LearningModule types.
//
// Note: module id is 'safety-before-speed'; its pillar is
// 'stability-protection' (per CurriculumMeta). Lesson 5 uses openSimulator
// with scenarioType 'custom' (a valid ScenarioKind).
// ─────────────────────────────────────────────────────────────────────────

import type {
	Lesson,
	LearningModule,
	ModuleAssessment,
} from '../LearningTypes';

const LESSON_1: Lesson = {
	id: 'safety-before-speed--stability-comes-before-speed',
	title: 'Stability comes before speed',
	description:
		'You learn why the first goal is not to move fast, but to stop one setback from wiping out your progress.',
	pillar: 'stability-protection',
	schoolsOfThought: [
		'risk-management',
		'personal-finance-basics',
		'life-design',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 1,
	tags: ['stability', 'risk', 'emergency-money', 'foundation'],
	prerequisites: [],
	relatedLessons: ['safety-before-speed--emergency-money-buys-time'],
	concepts: [
		'stability-before-growth',
		'downside-protection',
		'progress-defense',
	],
	blocks: [
		{
			type: 'paragraph',
			text: 'Money progress is not only about moving faster. It is also about not getting knocked back to zero. A higher income, a bigger savings rate, or a better investing plan can help, but none of it feels solid if one car repair, bill, layoff, or emergency wipes out the whole system.',
		},
		{
			type: 'paragraph',
			text: 'Stability means your money life has some shock absorbers. Not perfect protection. Not a guarantee that nothing bad happens. Just enough structure that normal life problems do not instantly become financial disasters. Safety before speed means you build the base before chasing the exciting move.',
		},
		{
			type: 'bullet_list',
			items: [
				'Cash for surprises',
				'Bills paid on time',
				'Debt pressure visible',
				'Basic protections in place',
				'A plan for bad months',
			],
		},
		{
			type: 'callout',
			icon: '🛡️',
			text: 'Speed feels good when life is calm. Stability matters when life gets rude.',
		},
		{
			type: 'paragraph',
			text: 'This matters because many people try to sprint with a fragile setup. They want to invest, upgrade, move, start something, buy something, or take a big swing, but their base is thin. That can work for a while, until one setback turns into debt, missed payments, panic, or a rushed decision.',
		},
		{
			type: 'example',
			label: 'Real example',
			text: 'Imagine two people each save $300 a month. One has no emergency cushion and high monthly pressure. The other has one month of basic expenses saved. When a $900 repair hits, the first person may lose months of progress or add debt. The second person still feels the hit, but the system survives.',
		},
		{
			type: 'paragraph',
			text: 'The common mistake is thinking stability is boring because it does not look impressive. A cash cushion does not feel as exciting as a big goal. Lowering risk does not give the same rush as chasing a fast win. But boring protection is what lets you stay in the game long enough for bigger progress to matter.',
		},
		{
			type: 'paragraph',
			text: 'Think of your money like a house. Income is the roof, investing might be the upper floor, and goals are the rooms you want to build. Stability is the foundation. You can decorate a weak house, but the first storm will show what was missing.',
		},
		{
			type: 'bullet_list',
			items: [
				'Protect the base',
				'Reduce fragile pressure',
				'Build a cushion',
				'Then speed up',
			],
		},
		{
			type: 'key_takeaway',
			text: 'Stability is not the opposite of progress; it is what keeps progress from disappearing.',
		},
	],
	audioScript:
		'Safety before speed is one of the least flashy money ideas, but it is one of the most important. Most people want to move fast. Save more. Invest more. Earn more. Buy the thing. Move out. Start the business. Take the leap. And those goals can be good. But if your base is fragile, speed can turn into stress really quickly. One car repair, one medical bill, one job issue, one family emergency, one bad month — and suddenly the whole plan gets knocked backward. Stability is not about making life perfect. It is about creating shock absorbers. Cash for surprises. Bills that are not constantly late. Debt pressure you can actually see. A plan for bad months. Some basic protection against the hits that could wreck your progress. Think about two people both saving three hundred dollars a month. One has no emergency cushion and already feels tight. The other has one month of basic expenses saved. A nine hundred dollar repair hits both of them. The first person may need debt, delay, or panic. The second person still hates the repair, but the system survives. That is the point. Stability does not remove pain. It keeps pain from becoming collapse. The mistake is thinking stability is boring because it does not look impressive. A cash cushion is not sexy. Lower fixed costs are not exciting. Having a plan for bad months will not impress strangers. But those things keep you in the game. Money is like a house. You can dream about the upper floors, the furniture, the view, the upgrades. But if the foundation is weak, the first storm exposes everything. Stability is the foundation. Speed comes later.',
	flashcards: [
		{
			id: 'safety-before-speed--stability-comes-before-speed--fc-main',
			role: 'main_idea',
			front: 'Why does stability come before speed?',
			back: 'Stability keeps normal setbacks from wiping out progress.',
		},
		{
			id: 'safety-before-speed--stability-comes-before-speed--fc-watch',
			role: 'watch_out',
			front: 'What is the fragile setup trap?',
			back: 'Trying to chase big goals while one emergency can still knock the whole system backward.',
		},
		{
			id: 'safety-before-speed--stability-comes-before-speed--fc-use',
			role: 'use_it',
			front: 'What does stability protect?',
			back: 'It protects your cash flow, runway, bills, and ability to keep making progress.',
		},
	],
	applyAction: {
		type: 'calculateRunway',
		label: 'Check your stability runway',
	},
	revision: 1,
};

const LESSON_2: Lesson = {
	id: 'safety-before-speed--emergency-money-buys-time',
	title: 'Emergency money buys time',
	description:
		'You learn how emergency money gives you time to respond instead of forcing rushed decisions.',
	pillar: 'stability-protection',
	schoolsOfThought: [
		'risk-management',
		'personal-finance-basics',
		'adulting-life-skills',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 1,
	tags: ['emergency-fund', 'cash', 'runway', 'stability'],
	prerequisites: ['safety-before-speed--stability-comes-before-speed'],
	relatedLessons: ['safety-before-speed--big-risks-need-a-plan'],
	concepts: ['emergency-buffer', 'response-time', 'cash-defense'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Emergency money is cash set aside for real surprises. Not vacation money. Not upgrade money. Not “I feel bored” money. It is the money that stands between a normal life problem and a desperate decision.',
		},
		{
			type: 'paragraph',
			text: 'The real thing emergency money buys is time. Time to fix the car without using bad debt. Time to cover a gap between jobs. Time to handle a bill without missing rent. Time to think before making a rushed move. The cash is useful because it creates space between problem and panic.',
		},
		{
			type: 'bullet_list',
			items: [
				'$250 can soften a small hit',
				'$500 can handle many minor surprises',
				'One month can change your posture',
				'Three months can change your choices',
			],
		},
		{
			type: 'callout',
			icon: '⏱️',
			text: 'Emergency money does not make bad things good. It gives you time to respond like an adult instead of react like a cornered person.',
		},
		{
			type: 'paragraph',
			text: 'A lot of people delay emergency savings because the target feels too large. They hear “three to six months” and shut down. That is understandable, but it is also dangerous. You do not need the perfect cushion to start getting safer. The first $250 matters. The first $500 matters. The first month matters.',
		},
		{
			type: 'example',
			label: 'Realistic start',
			text: 'If someone can set aside $50 every paycheck, that may feel small. But after five paychecks, they have $250. After ten, $500. That money might cover a tire, a bill gap, medication, a work expense, or a small repair without turning into credit card debt.',
		},
		{
			type: 'paragraph',
			text: 'The common mistake is treating emergency money like idle money. People think, “It is just sitting there.” But sitting there is the job. A seatbelt is useless most days too. Then one day it matters a lot. Cash protection works the same way.',
		},
		{
			type: 'paragraph',
			text: 'Think of emergency money like a fire extinguisher. You do not buy it because you want a fire. You keep it because if a fire starts, the first few minutes matter. Emergency cash is not meant to maximize growth. It is meant to keep small fires from burning down the whole house.',
		},
		{
			type: 'bullet_list',
			items: [
				'Pick a starter target',
				'Keep it separate',
				'Use it for real surprises',
				'Rebuild it after use',
			],
		},
		{
			type: 'key_takeaway',
			text: 'Emergency money buys time, and time is what keeps problems from becoming panic.',
		},
	],
	audioScript:
		'Emergency money buys time. That is the real point. People call it an emergency fund, but the cash itself is only part of the story. What it really gives you is space between a problem and a reaction. Your car needs a tire. Your hours get cut. A bill hits earlier than expected. Something breaks. Without emergency money, you may be forced into debt, missed payments, borrowing from someone you did not want to ask, or making a rushed decision. With even a small cushion, the same problem feels different. Not fun. Not easy. But different. You can respond instead of panic. A lot of people avoid this because the target sounds too big. Three months. Six months. It feels impossible, so they do nothing. But the first layer matters. Two hundred fifty dollars matters. Five hundred dollars matters. One month of basic expenses matters. If you set aside fifty dollars every paycheck, five paychecks gets you two hundred fifty. Ten paychecks gets you five hundred. That might cover a tire, a small bill gap, medication, or a work expense without turning into credit card debt. The common mistake is thinking emergency money is wasted because it is “just sitting there.” But sitting there is the job. A seatbelt is boring until the crash. A fire extinguisher is boring until the fire. Emergency cash is the same. You are not keeping it because you want something bad to happen. You are keeping it because life sometimes does happen, and the first few minutes matter. The goal is simple: pick a starter target, keep it separate, use it for real surprises, and rebuild it when you use it. Emergency money is not speed. It is defense.',
	flashcards: [
		{
			id: 'safety-before-speed--emergency-money-buys-time--fc-main',
			role: 'main_idea',
			front: 'What does emergency money buy?',
			back: 'Emergency money buys time to respond instead of making rushed decisions.',
		},
		{
			id: 'safety-before-speed--emergency-money-buys-time--fc-watch',
			role: 'watch_out',
			front: 'Why is “it just sits there” wrong?',
			back: 'Sitting there is the job; emergency cash protects you when problems hit.',
		},
		{
			id: 'safety-before-speed--emergency-money-buys-time--fc-use',
			role: 'use_it',
			front: 'What is a useful starter target?',
			back: 'Start with a reachable cushion like $250 or $500, then build toward one month of basics.',
		},
	],
	applyAction: {
		type: 'calculateRunway',
		label: 'Calculate your emergency runway',
	},
	revision: 1,
};

const LESSON_3: Lesson = {
	id: 'safety-before-speed--big-risks-need-a-plan',
	title: 'Big risks need a plan',
	description:
		'You learn how to identify the big risks that could damage your cash flow, time, health, housing, or options.',
	pillar: 'stability-protection',
	schoolsOfThought: [
		'risk-management',
		'adulting-life-skills',
		'personal-finance-basics',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['risk', 'protection', 'insurance', 'adulting'],
	prerequisites: ['safety-before-speed--emergency-money-buys-time'],
	relatedLessons: ['safety-before-speed--scams-target-urgency'],
	concepts: ['big-risk-mapping', 'downside-events', 'protection-planning'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Some money problems are annoying. Other money problems are large enough to change your whole life. Big risks are the events that can damage your cash flow, housing, health, transportation, job, family responsibilities, or ability to keep going. They deserve more than wishful thinking.',
		},
		{
			type: 'paragraph',
			text: 'This matters because a good money plan is not only about growth. It also asks, “What could knock me off track?” That question is not fear-based. It is mature. If you know the risks, you can think about buffers, documents, coverage, backup plans, and next steps before the pressure is already high.',
		},
		{
			type: 'bullet_list',
			items: [
				'Job interruption',
				'Medical costs',
				'Car problems',
				'Housing disruption',
				'Family emergencies',
				'Identity theft or fraud',
			],
		},
		{
			type: 'callout',
			icon: '🚧',
			text: 'Risk management is not paranoia. It is admitting that real life has potholes.',
		},
		{
			type: 'paragraph',
			text: 'Protections can look different depending on the situation. Cash buffers can help with short-term shocks. Insurance can help transfer certain large risks. Documents can reduce chaos. Strong passwords and account alerts can lower fraud damage. Backup income options can reduce job risk. None of these make life perfectly safe, but they can lower the chance that one event wrecks everything.',
		},
		{
			type: 'example',
			label: 'Simple risk map',
			text: 'If someone depends on a car to get to work, car trouble is not just a car problem. It is an income problem. A useful plan might include a repair buffer, knowing the insurance basics, keeping important documents accessible, and having a temporary transportation backup.',
		},
		{
			type: 'paragraph',
			text: 'The common mistake is only preparing for the risks that are easy to imagine. People may worry about market crashes but ignore the more immediate risk of a missed paycheck, broken car, stolen card, medical bill, or housing issue. The closest risks often matter first.',
		},
		{
			type: 'paragraph',
			text: 'Think of protection like guardrails. Guardrails do not make the road perfect. They do not drive for you. But when something goes wrong, they reduce how far off course you go. A stable money life has guardrails around the parts that would hurt most if they broke.',
		},
		{
			type: 'bullet_list',
			items: [
				'Name your top three risks',
				'Ask what each risk would cost',
				'List what would reduce the damage',
				'Pick one protection to improve',
			],
		},
		{
			type: 'key_takeaway',
			text: 'Big risks need a plan because one bad event can undo months of progress if nothing is protecting the base.',
		},
	],
	audioScript:
		'Big risks need a plan. Not because you should live scared, but because life has potholes. Some money problems are annoying. Other problems can change your whole direction. A job interruption. A medical bill. A car breakdown. A housing issue. A family emergency. Fraud. Identity theft. These are not just random inconveniences. They can hit your cash flow, your time, your health, your transportation, your housing, and your options. A real money plan asks, “What could knock me off track?” That question is not negative. It is mature. Growth matters, but protection matters too. Different risks need different defenses. Cash can help with short-term shocks. Insurance can transfer certain large risks. Strong passwords and account alerts can limit fraud damage. Important documents can reduce chaos. Backup transportation or backup income ideas can matter if one thing breaking would threaten your paycheck. None of this makes life perfectly safe. That is not the promise. The point is to reduce damage. Think about someone who needs their car to get to work. A car problem is not just a car problem. It can become an income problem. So a useful risk map might include a small repair buffer, basic coverage awareness, important documents, and a temporary transportation backup. The mistake is preparing for dramatic risks while ignoring close ones. People worry about giant abstract disasters but ignore the broken tire, missed paycheck, stolen card, or rent issue that could hit first. Think of protection like guardrails. They do not make the road perfect. They do not drive for you. But if something goes wrong, they reduce how far off course you go.',
	flashcards: [
		{
			id: 'safety-before-speed--big-risks-need-a-plan--fc-main',
			role: 'main_idea',
			front: 'What is a big risk?',
			back: 'A big risk is an event that could damage your cash flow, housing, health, job, or options.',
		},
		{
			id: 'safety-before-speed--big-risks-need-a-plan--fc-watch',
			role: 'watch_out',
			front: 'What risk mistake do people make?',
			back: 'They prepare for dramatic risks while ignoring closer risks like car trouble, missed paychecks, or fraud.',
		},
		{
			id: 'safety-before-speed--big-risks-need-a-plan--fc-use',
			role: 'use_it',
			front: 'How do you map a risk?',
			back: 'Name the risk, estimate the damage, list protections, and improve one guardrail.',
		},
	],
	applyAction: {
		type: 'noToolAction',
		label: 'Map your top three risks',
		prompt: 'Write the three events most likely to disrupt your money life, then name one protection that would reduce the damage from each.',
	},
	revision: 1,
};

const LESSON_4: Lesson = {
	id: 'safety-before-speed--scams-target-urgency',
	title: 'Scams target urgency',
	description:
		'You learn why scams often work by rushing your emotions before your judgment can catch up.',
	pillar: 'stability-protection',
	schoolsOfThought: ['anti-scam', 'risk-management', 'behavioral-finance'],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['scams', 'fraud', 'pressure', 'safety'],
	prerequisites: ['safety-before-speed--big-risks-need-a-plan'],
	relatedLessons: ['safety-before-speed--stress-test-before-you-speed-up'],
	concepts: ['urgency-manipulation', 'scam-pressure', 'fraud-defense'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Scams rarely start by looking stupid. Many scams work because they target urgency, fear, greed, shame, loneliness, or trust. The goal is to make you move before you slow down. If someone can rush your emotions, they can get around your judgment.',
		},
		{
			type: 'paragraph',
			text: 'This matters because scams do not only attack “gullible” people. They attack human pressure points. A smart person can still make a bad move when scared, rushed, tired, hopeful, embarrassed, or desperate. Scam defense starts with respecting how manipulation works.',
		},
		{
			type: 'bullet_list',
			items: [
				'Act now',
				'Keep this secret',
				'Send money first',
				'Click this link',
				'Verify your account',
				'This return is special',
			],
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'Urgency is the scammer’s favorite shortcut around your thinking brain.',
		},
		{
			type: 'paragraph',
			text: 'A good safety rule is to slow the moment down. Do not click because the message feels official. Do not send money because the story feels emotional. Do not share codes because someone sounds convincing. Do not trust an investment pitch because it promises fast upside. Stop, verify through a separate channel, and give your brain time to re-enter the room.',
		},
		{
			type: 'example',
			label: 'Common setup',
			text: 'You get a message saying your account will be locked unless you act now. The link looks official. The timer feels stressful. A safer move is to avoid the link, open the company’s app or website directly, and check there. Real problems can usually survive a few minutes of verification.',
		},
		{
			type: 'paragraph',
			text: 'The common mistake is thinking you will “just know” a scam when you see it. That confidence can become its own weakness. Modern scams can copy logos, use real names, spoof numbers, and create believable stories. The defense is not ego. The defense is a repeatable pause.',
		},
		{
			type: 'paragraph',
			text: 'Think of scam pressure like someone trying to make you sign a contract while the room is on fire. The first job is not to understand every detail. The first job is to leave the fake emergency. Slow down. Verify. Ask someone trusted. Use official channels. Refuse secrecy.',
		},
		{
			type: 'bullet_list',
			items: [
				'Pause',
				'Do not click links under pressure',
				'Verify separately',
				'Never share one-time codes',
				'Refuse secrecy',
			],
		},
		{
			type: 'key_takeaway',
			text: 'If a money decision demands urgency, secrecy, or blind trust, slow down before you move.',
		},
	],
	audioScript:
		'Scams target urgency. That is the first thing to understand. A lot of people think scams work because victims are dumb. That is not fair, and it is not accurate enough to protect you. Scams work because they hit human pressure points: fear, greed, shame, loneliness, trust, exhaustion, hope, desperation. The scammer wants you moving before your judgment catches up. Act now. Keep this secret. Send money first. Click this link. Verify your account. This opportunity is special. That kind of pressure is not random. It is the tool. So the first defense is not being a genius. The first defense is slowing the moment down. If a message says your account will be locked unless you click now, do not use the link in the message. Open the company’s app or website directly and check there. If someone asks for a one-time code, pause. If someone says not to tell anyone, pause. If an investment pitch promises fast upside with no real downside, pause. Real problems can usually survive a few minutes of verification. Scams often cannot. The common mistake is thinking you will just know. But modern scams can copy logos, spoof numbers, use real names, and sound official. Confidence is not enough. You need a rule. Think of scam pressure like someone trying to make you sign a contract while the room is on fire. The first job is to leave the fake emergency. Slow down. Verify separately. Ask someone trusted. Use official channels. Refuse secrecy. If a money decision requires urgency, secrecy, or blind trust, your answer is pause.',
	flashcards: [
		{
			id: 'safety-before-speed--scams-target-urgency--fc-main',
			role: 'main_idea',
			front: 'What do scams target?',
			back: 'Scams often target urgency, fear, greed, shame, loneliness, or trust.',
		},
		{
			id: 'safety-before-speed--scams-target-urgency--fc-watch',
			role: 'watch_out',
			front: 'What is dangerous scam confidence?',
			back: 'Believing you will always “just know” a scam when modern scams can look official.',
		},
		{
			id: 'safety-before-speed--scams-target-urgency--fc-use',
			role: 'use_it',
			front: 'What should you do under pressure?',
			back: 'Pause, verify separately, avoid pressured links, refuse secrecy, and do not share one-time codes.',
		},
	],
	applyAction: {
		type: 'noToolAction',
		label: 'Write your anti-scam pause rule',
		prompt: 'Write one rule you will follow whenever a money message creates urgency, secrecy, or pressure.',
	},
	revision: 1,
};

const LESSON_5: Lesson = {
	id: 'safety-before-speed--stress-test-before-you-speed-up',
	title: 'Stress-test before you speed up',
	description:
		'You learn how to check whether a money move is safe enough before increasing risk, spending, or commitment.',
	pillar: 'stability-protection',
	schoolsOfThought: [
		'risk-management',
		'life-design',
		'personal-finance-basics',
	],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 2,
	tags: ['stress-test', 'risk', 'decisions', 'safety'],
	prerequisites: ['safety-before-speed--scams-target-urgency'],
	relatedLessons: ['safety-before-speed--stability-comes-before-speed'],
	concepts: [
		'stress-testing-decisions',
		'margin-of-safety',
		'fragile-commitments',
	],
	blocks: [
		{
			type: 'paragraph',
			text: 'A stress test asks what happens if life does not go perfectly. What if income drops? What if a bill arrives early? What if the car breaks? What if the job takes longer to replace than expected? What if the payment feels fine now but heavy later? Stress-testing does not mean expecting failure. It means checking whether your plan can survive normal friction.',
		},
		{
			type: 'paragraph',
			text: 'This matters before big commitments. Moving out, taking on a car payment, starting school, changing jobs, adding debt, investing more aggressively, or upgrading lifestyle can all make sense in the right context. But if the plan only works when nothing goes wrong, it may be too fragile.',
		},
		{
			type: 'bullet_list',
			items: [
				'What if income drops 10%?',
				'What if expenses rise $200?',
				'What if an emergency costs $1,000?',
				'What if the timeline takes twice as long?',
			],
		},
		{
			type: 'callout',
			icon: '🧪',
			text: 'A plan that only works in perfect conditions is not a plan. It is a hope.',
		},
		{
			type: 'paragraph',
			text: 'A good stress test focuses on margin of safety. Margin of safety means extra room between your plan and the edge. If your rent, car, debt, and bills leave you with $25 of monthly breathing room, one small problem can break the plan. If you have $300 of breathing room and a cash cushion, the same problem may be manageable.',
		},
		{
			type: 'example',
			label: 'Before the upgrade',
			text: 'Suppose someone wants a $350 monthly car payment. The payment might be technically possible. But a stress test asks: after insurance, gas, repairs, and the payment, what happens to the monthly gap? If one repair or missed shift breaks the plan, the real cost is bigger than $350.',
		},
		{
			type: 'paragraph',
			text: 'The common mistake is confusing approval with safety. A lender, landlord, school, or payment plan may approve you for something that still squeezes your life too hard. Approval means you meet someone else’s threshold. It does not mean the decision fits your whole money system.',
		},
		{
			type: 'paragraph',
			text: 'Think of stress-testing like stepping on a bridge before driving across it. You are not being negative. You are checking the structure. If the bridge feels weak, you do not shame yourself for noticing. You reinforce it, choose a lighter load, or take a different route.',
		},
		{
			type: 'bullet_list',
			items: [
				'Check the monthly gap',
				'Check the cash cushion',
				'Check the worst normal case',
				'Delay or shrink the move if needed',
			],
		},
		{
			type: 'paragraph',
			text: 'This lesson does not say “never take risks.” A life with no risk is not realistic. The goal is better risk. Risks that you understand. Risks with backup plans. Risks where a bad month hurts, but does not destroy the whole base.',
		},
		{
			type: 'key_takeaway',
			text: 'Before you speed up, test whether your plan can survive a normal bad month.',
		},
	],
	audioScript:
		'Stress-test before you speed up. That is the move. A stress test just asks, “What happens if life does not go perfectly?” What if income drops ten percent? What if expenses rise two hundred dollars? What if an emergency costs one thousand? What if the timeline takes twice as long? This is not negative thinking. It is responsible thinking. Big money moves can make sense. Moving out. Getting a car. Changing jobs. Starting school. Adding debt. Investing more. Upgrading your lifestyle. None of those are automatically wrong. But if the plan only works when everything goes perfectly, it is fragile. A good stress test looks for margin of safety. That means extra room between your plan and the edge. If your bills leave you with twenty-five dollars of monthly breathing room, almost anything can break the system. If you have a few hundred dollars of room and some emergency money, the same problem may hurt but not destroy you. Think about a three hundred fifty dollar car payment. The payment might be approved. It might even be technically possible. But after insurance, gas, repairs, and the payment, what happens to the monthly gap? What happens if a shift gets cut? What happens if the car needs work? The common mistake is confusing approval with safety. A lender, landlord, school, or payment plan may approve you for something that still squeezes your life too hard. Approval is someone else’s threshold. It is not the same as a healthy decision for your whole money system. Stress-testing is like checking a bridge before driving across it. You are not being scared. You are checking the structure. The goal is not to avoid every risk. The goal is to take cleaner risks — risks with room, backup plans, and a base that survives a normal bad month.',
	flashcards: [
		{
			id: 'safety-before-speed--stress-test-before-you-speed-up--fc-main',
			role: 'main_idea',
			front: 'What is a stress test?',
			back: 'A stress test checks whether a plan can survive normal problems like income drops, higher expenses, or emergencies.',
		},
		{
			id: 'safety-before-speed--stress-test-before-you-speed-up--fc-watch',
			role: 'watch_out',
			front: 'Why is approval not safety?',
			back: 'Approval means you met someone else’s threshold; it does not mean the decision fits your whole money system.',
		},
		{
			id: 'safety-before-speed--stress-test-before-you-speed-up--fc-use',
			role: 'use_it',
			front: 'What should you check before speeding up?',
			back: 'Check your monthly gap, cash cushion, worst normal case, and whether the move needs to be delayed or reduced.',
		},
	],
	applyAction: {
		type: 'openSimulator',
		label: 'Stress-test a money move',
		scenarioType: 'custom',
	},
	revision: 1,
};

const ASSESSMENT: ModuleAssessment = {
	moduleId: 'safety-before-speed',
	checkpointQuiz: [
		{
			type: 'multiple_choice',
			id: 'safety-before-speed--cq--q1',
			question: 'What is the main idea of “safety before speed”?',
			options: [
				'Never try to grow your money',
				'Build stability so setbacks do not erase progress',
				'Avoid all risk forever',
				'Only focus on investing',
			],
			correctIndex: 1,
			explanation:
				'Safety before speed means protecting the base before chasing faster progress.',
		},
		{
			type: 'true_false',
			id: 'safety-before-speed--cq--q2',
			question: 'Stability is the opposite of progress.',
			correct: false,
			explanation: 'Stability helps progress survive normal setbacks.',
		},
		{
			type: 'multiple_choice',
			id: 'safety-before-speed--cq--q3',
			question: 'What does emergency money mainly buy?',
			options: [
				'Status',
				'Time to respond instead of panic',
				'Guaranteed wealth',
				'A higher credit score automatically',
			],
			correctIndex: 1,
			explanation:
				'Emergency cash creates space between a problem and a rushed decision.',
		},
		{
			type: 'scenario',
			id: 'safety-before-speed--cq--q4',
			scenario:
				'Alex says, “I only have $250 saved, so it does not matter.”',
			question: 'What is the best response from this module?',
			options: [
				'$250 is useless',
				'$250 can still soften small hits and prevent worse decisions',
				'Alex should spend it because it is small',
				'Emergency money only matters after three full months',
			],
			correctIndex: 1,
			explanation:
				'Small starter cushions matter because they reduce panic and protect the base.',
		},
		{
			type: 'multiple_choice',
			id: 'safety-before-speed--cq--q5',
			question: 'Why is emergency money not “idle” money?',
			options: [
				'Because it is supposed to chase high returns',
				'Because sitting ready is its job',
				'Because it should be spent quickly',
				'Because it replaces income forever',
			],
			correctIndex: 1,
			explanation:
				'Emergency money is like a seatbelt or fire extinguisher: boring until it matters.',
		},
		{
			type: 'multiple_choice',
			id: 'safety-before-speed--cq--q6',
			question: 'What is a big risk?',
			options: [
				'Any purchase over $20',
				'An event that could damage your cash flow, housing, health, job, or options',
				'A planned grocery bill',
				'A fun category in your budget',
			],
			correctIndex: 1,
			explanation:
				'Big risks are events that could knock your money life off track.',
		},
		{
			type: 'scenario',
			id: 'safety-before-speed--cq--q7',
			scenario:
				'Maya depends on her car to get to work but has no repair buffer or backup transportation plan.',
			question: 'What does Module 5 suggest noticing?',
			options: [
				'Car trouble is only a car problem',
				'Car trouble could become an income problem',
				'Maya should ignore the risk until it happens',
				'Risk planning is always fear-based',
			],
			correctIndex: 1,
			explanation:
				'If transportation is tied to income, the risk is bigger than the repair itself.',
		},
	],
	finalTest: [
		{
			type: 'multiple_choice',
			id: 'safety-before-speed--final--q1',
			question: 'Which statement best captures Module 5?',
			options: [
				'Speed matters more than safety',
				'Stability protects progress from normal life shocks',
				'Scams only target foolish people',
				'Emergency money is wasted if unused',
			],
			correctIndex: 1,
			explanation:
				'The module teaches protection, stability, and stress-testing before speeding up.',
		},
		{
			type: 'true_false',
			id: 'safety-before-speed--final--q2',
			question:
				'A money plan can be fragile even if it looks good when everything goes perfectly.',
			correct: true,
			explanation:
				'A plan that only works in perfect conditions may fail under normal friction.',
		},
		{
			type: 'multiple_choice',
			id: 'safety-before-speed--final--q3',
			question: 'What is a shock absorber in a money life?',
			options: [
				'A random impulse purchase',
				'Emergency cash, visible bills, and basic protections',
				'A payment you barely understand',
				'A high-pressure sales pitch',
			],
			correctIndex: 1,
			explanation: 'Shock absorbers reduce the damage from setbacks.',
		},
		{
			type: 'scenario',
			id: 'safety-before-speed--final--q4',
			scenario:
				'Jordan wants to invest more but has no cash cushion and one emergency would go straight onto a credit card.',
			question: 'What is the best module-based read?',
			options: [
				'Jordan may need more stability before adding speed',
				'Jordan should ignore emergencies',
				'Jordan is guaranteed to be fine',
				'Cash protection never matters',
			],
			correctIndex: 0,
			explanation:
				'The module does not reject growth, but it says the base should survive normal setbacks.',
		},
		{
			type: 'multiple_choice',
			id: 'safety-before-speed--final--q5',
			question: 'Why can a $500 emergency cushion matter?',
			options: [
				'It solves every financial issue',
				'It can handle or soften many minor surprises',
				'It guarantees no debt ever again',
				'It replaces insurance',
			],
			correctIndex: 1,
			explanation:
				'A starter cushion can stop small problems from turning into larger ones.',
		},
		{
			type: 'scenario',
			id: 'safety-before-speed--final--q6',
			scenario:
				'Renee gets a text saying her account will be locked unless she clicks a link immediately.',
			question: 'What is the safest lesson-based response?',
			options: [
				'Click quickly before thinking',
				'Open the official app or site separately and verify there',
				'Send a code if asked',
				'Keep it secret',
			],
			correctIndex: 1,
			explanation:
				'Scam defense starts with slowing down and verifying through separate official channels.',
		},
		{
			type: 'multiple_choice',
			id: 'safety-before-speed--final--q7',
			question:
				'What is the scammer’s favorite shortcut around judgment?',
			options: [
				'Patience',
				'Urgency',
				'Clear explanations',
				'Independent verification',
			],
			correctIndex: 1,
			explanation:
				'Urgency pressures you to move before your judgment catches up.',
		},
		{
			type: 'true_false',
			id: 'safety-before-speed--final--q8',
			question:
				'Being smart means you can always recognize a scam instantly.',
			correct: false,
			explanation:
				'Modern scams can look official and target emotion, so repeatable pause rules matter.',
		},
		{
			type: 'multiple_choice',
			id: 'safety-before-speed--final--q9',
			question: 'What is margin of safety?',
			options: [
				'Extra room between your plan and the edge',
				'A guarantee that nothing bad happens',
				'A reason to avoid every risk',
				'A type of subscription',
			],
			correctIndex: 0,
			explanation:
				'Margin of safety gives a plan room to survive imperfect conditions.',
		},
		{
			type: 'scenario',
			id: 'safety-before-speed--final--q10',
			scenario:
				'Chris is approved for a payment plan, but after the payment their monthly gap would be only $30.',
			question: 'What does Module 5 warn?',
			options: [
				'Approval automatically means safety',
				'The payment may be too fragile for the whole money system',
				'A $30 gap is always enough',
				'Stress-testing is unnecessary',
			],
			correctIndex: 1,
			explanation:
				'Approval is someone else’s threshold; the monthly gap shows whether the plan fits the user’s system.',
		},
		{
			type: 'multiple_choice',
			id: 'safety-before-speed--final--q11',
			question: 'What should a stress test ask?',
			options: [
				'What if income drops, expenses rise, or an emergency hits?',
				'How can I avoid thinking about bad months?',
				'Will this impress people?',
				'Can I make the first payment only?',
			],
			correctIndex: 0,
			explanation:
				'Stress-testing checks normal bad-month scenarios before speeding up.',
		},
		{
			type: 'scenario',
			id: 'safety-before-speed--final--q12',
			scenario:
				'Taylor wants to improve safety but feels overwhelmed by everything that could go wrong.',
			question: 'What is the best first action?',
			options: [
				'Try to solve every risk today',
				'Name the top three risks and improve one guardrail',
				'Ignore the risks completely',
				'Assume preparation is paranoia',
			],
			correctIndex: 1,
			explanation:
				'Risk management starts with naming likely risks and improving one practical protection at a time.',
		},
	],
};

export const SAFETY_BEFORE_SPEED_MODULE: LearningModule = {
	id: 'safety-before-speed',
	title: 'Safety Before Speed',
	description:
		'Before chasing growth, build the base that keeps one setback from wiping you out: emergency money, risk planning, scam defense, and stress-testing your moves before you commit.',
	emoji: '🛡️',
	accentColor: '#c8ff00',
	tier: 'stability',
	pillar: 'stability-protection',
	order: 5,
	lessons: [LESSON_1, LESSON_2, LESSON_3, LESSON_4, LESSON_5],
	assessment: ASSESSMENT,
};
